import { MASS_SPECS } from '../core/constants';
import { mulberry32 } from '../core/rng';
import type { MassKind } from '../core/types';
import { INK, VOID } from './palette';

/**
 * Masses drawn like engraved, stippled spheres from old astronomy plates. Sprites are rendered
 * once per (kind, on-screen radius, device pixel ratio) and cached; they are always drawn upright
 * so the light falls from the upper left even when the plate is rotated.
 */

const cache = new Map<string, HTMLCanvasElement>();

/** Extra room around the body for rays, halos and rings (in body radii). */
export const SPRITE_PAD: Record<MassKind, number> = {
  moon: 1.25,
  star: 1.75,
  giant: 1.25,
  hole: 1.6,
  void: 3.2,
};

function canvas(size: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = Math.max(2, Math.ceil(size));
  return c;
}

const LIGHT = (() => {
  const v = [-0.7, -0.58, 0.42];
  const n = Math.hypot(v[0]!, v[1]!, v[2]!);
  return v.map((x) => x / n) as [number, number, number];
})();

/** Stipple a lit sphere of radius r (device px) centred at (cx, cy). */
function stippleSphere(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  dot: number,
  density: number,
  seed: number,
  bias = 0,
) {
  const rand = mulberry32(seed);
  const n = Math.round(Math.PI * r * r * density);
  ctx.fillStyle = INK;
  for (let i = 0; i < n; i++) {
    const a = rand.next() * Math.PI * 2;
    const rr = Math.sqrt(rand.next());
    const u = Math.cos(a) * rr;
    const v = Math.sin(a) * rr;
    const z = Math.sqrt(Math.max(0, 1 - u * u - v * v));
    const lam = Math.max(0, u * LIGHT[0] + v * LIGHT[1] + z * LIGHT[2]);
    const rim = Math.pow(1 - z, 3) * 0.35; // faint reflected light at the limb
    const p = Math.min(1, 0.02 + 0.98 * Math.pow(lam, 1.7) + rim + bias);
    if (rand.next() > p) continue;
    ctx.globalAlpha = 0.55 + 0.45 * rand.next();
    ctx.beginPath();
    ctx.arc(cx + u * r, cy + v * r, dot * (0.7 + 0.5 * rand.next()), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function outline(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  w: number,
  alpha: number,
) {
  ctx.strokeStyle = INK;
  ctx.globalAlpha = alpha;
  ctx.lineWidth = w;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/**
 * Sprite for a mass whose body has radius `rPx` CSS pixels on screen.
 * Returns the canvas and the scale between canvas pixels and CSS pixels.
 */
export function massSprite(kind: MassKind, rPx: number, dpr: number): HTMLCanvasElement {
  const key = `${kind}:${rPx.toFixed(2)}:${dpr}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const r = rPx * dpr;
  const size = r * 2 * SPRITE_PAD[kind] + 4;
  const c = canvas(size);
  const ctx = c.getContext('2d')!;
  const cx = c.width / 2;
  const cy = c.height / 2;
  // Dot size grows a little with the sphere; density is set for about half coverage where lit.
  const dot = Math.max(
    0.55,
    Math.min(0.85 * dpr, 0.42 * dpr * Math.max(0.85, Math.min(1.5, rPx / 12))),
  );
  const dens = (coverage: number) => coverage / (Math.PI * dot * dot);
  const w = Math.max(0.8, 0.9 * dpr);
  ctx.lineCap = 'round';

  if (kind === 'moon') {
    // A dark disc first so beams behind read as blocked, then stipple, craters and the outline.
    ctx.fillStyle = 'rgba(6, 22, 44, 0.85)';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    stippleSphere(ctx, cx, cy, r, dot, dens(1.25), 11);
    const rand = mulberry32(7);
    for (let i = 0; i < 4; i++) {
      const a = rand.next() * Math.PI * 2;
      const d = rand.next() * 0.6 * r;
      const cr = r * (0.12 + rand.next() * 0.12);
      ctx.strokeStyle = INK;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = w * 0.8;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * d, cy + Math.sin(a) * d, cr, Math.PI * 0.85, Math.PI * 1.95);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    outline(ctx, cx, cy, r, w, 0.9);
  } else if (kind === 'star') {
    // Rays like an engraved star: alternating long and short strokes.
    const rays = 16;
    for (let i = 0; i < rays; i++) {
      const a = (i / rays) * Math.PI * 2 + 0.1;
      const long = i % 2 === 0;
      const r1 = r * 1.12;
      const r2 = r * (long ? 1.68 : 1.36);
      ctx.strokeStyle = INK;
      ctx.globalAlpha = long ? 0.7 : 0.45;
      ctx.lineWidth = w * (long ? 1 : 0.7);
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(6, 22, 44, 0.85)';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    stippleSphere(ctx, cx, cy, r, dot, dens(1.6), 23, 0.15);
    outline(ctx, cx, cy, r, w, 0.95);
  } else if (kind === 'giant') {
    ctx.fillStyle = 'rgba(6, 22, 44, 0.85)';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    stippleSphere(ctx, cx, cy, r, dot, dens(1.15), 37);
    // Cloud bands: curved latitude lines clipped to the disc.
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = INK;
    ctx.lineWidth = w * 0.85;
    for (const [lat, alpha] of [
      [-0.62, 0.35],
      [-0.3, 0.55],
      [-0.05, 0.4],
      [0.22, 0.6],
      [0.5, 0.4],
    ] as const) {
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.ellipse(
        cx,
        cy + lat * r,
        r * Math.sqrt(1 - lat * lat) * 1.02,
        r * 0.16,
        -0.12,
        0,
        Math.PI,
      );
      ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
    outline(ctx, cx, cy, r, w, 0.9);
  } else if (kind === 'hole') {
    // A perfect void: soft shadow, the disc itself, and a thin photon ring.
    const g = ctx.createRadialGradient(cx, cy, r * 0.9, cx, cy, r * 1.6);
    g.addColorStop(0, 'rgba(0, 4, 12, 0.55)');
    g.addColorStop(1, 'rgba(0, 4, 12, 0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = VOID;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffe9bd';
    ctx.lineWidth = Math.max(1, 1.1 * dpr);
    ctx.globalAlpha = 0.95;
    ctx.beginPath();
    ctx.arc(cx, cy, r + ctx.lineWidth * 0.6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = Math.max(0.6, 0.6 * dpr);
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.14, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  } else {
    // Void (negative mass): a bleached absence with a hollow core; arrows push outwards.
    const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 2.6);
    halo.addColorStop(0, 'rgba(205, 228, 255, 0.24)');
    halo.addColorStop(0.42, 'rgba(205, 228, 255, 0.12)');
    halo.addColorStop(1, 'rgba(205, 228, 255, 0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 2.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = w;
    ctx.setLineDash([2.2 * dpr, 2.4 * dpr]);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.38, 0, Math.PI * 2);
    ctx.stroke();
    const n = 8;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.PI / 8;
      const r1 = r * 1.45;
      const r2 = r * 2.35;
      const hx = cx + Math.cos(a) * r2;
      const hy = cy + Math.sin(a) * r2;
      const hs = r * 0.36;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      ctx.lineTo(hx, hy);
      ctx.moveTo(hx + Math.cos(a + 2.5) * hs, hy + Math.sin(a + 2.5) * hs);
      ctx.lineTo(hx, hy);
      ctx.lineTo(hx + Math.cos(a - 2.5) * hs, hy + Math.sin(a - 2.5) * hs);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
  cache.set(key, c);
  return c;
}

/** Draw a mass sprite centred at screen (x, y) CSS px. */
export function drawMass(
  ctx: CanvasRenderingContext2D,
  kind: MassKind,
  x: number,
  y: number,
  scale: number,
  dpr: number,
  alpha = 1,
): void {
  const rPx = MASS_SPECS[kind].radius * scale;
  const sprite = massSprite(kind, rPx, dpr);
  const size = sprite.width / dpr;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(sprite, x - size / 2, y - size / 2, size, size);
  ctx.restore();
}

/** Small standalone icon canvas for the tray. */
export function trayIcon(kind: MassKind, cssSize: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = Math.round(cssSize * dpr);
  c.style.width = c.style.height = `${cssSize}px`;
  const ctx = c.getContext('2d')!;
  const rPx = cssSize / 2 / SPRITE_PAD[kind] - 1;
  const sprite = massSprite(kind, rPx, dpr);
  ctx.drawImage(sprite, (c.width - sprite.width) / 2, (c.height - sprite.height) / 2);
  return c;
}
